import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertService } from '../../../../services/alert.service';
import { CleanDeliveryOrder } from '../../../../export-types/cleaned-types';
import CreatableSelect from 'react-select/creatable';
import { InputWrapper } from '../../../common/input-wrapper';
import { MediaInput, MediaInputValue } from '../../../common/media-input';
import { DatePicker } from 'rsuite';
import moment from 'moment';
import { RawDateValue } from '../../../../utils/date-value';
import { required } from '../../../../utils/validations';
import { useDeliveryStorageLocation } from '../../../../api/delivery';
import { uploadMediaInputFiles } from '../../../../services/file-service';
import { api } from '../../../../services/api';

type Form = {
  date: Date;
  storageLocation?: { value: string; label: string };
  packedFiles?: MediaInputValue[];
};

type Props = {
  close: () => void;
  reject: () => void;
  deliveryOrders: CleanDeliveryOrder[];
};

export const MarkAsReceivedBulkModal: React.FC<Props> = (props) => {
  const {
    control,
    formState: { isSubmitting, errors },
    handleSubmit,
  } = useForm<Form>({
    defaultValues: {
      date: new Date(),
    },
  });

  const { data: storageLocations } = useDeliveryStorageLocation();
  const [inputStorage, setInputStorage] = useState('');

  const intl = useIntl();

  const onSubmit = handleSubmit(async (form) => {
    const { packedFiles, ...data } = form;

    const packedFilesResponse = await uploadMediaInputFiles(
      form.packedFiles || [],
    );
    const isoDateString = moment(data.date, 'DD.MM.YYYY HH:mm').toISOString();

    await api.post(`/transportation/receive/bulk`, {
      deliveryOrderIds: props.deliveryOrders.map((order) => {
        return order.id;
      }),
      packedFiles: packedFilesResponse.map((data) => data.file),
      storageLocation: data.storageLocation?.value,
      date: isoDateString,
    });

    AlertService.success();
    props.close();
  });
  return (
    <div className="modal-dialog">
      <form className="modal-content" onSubmit={onSubmit}>
        <div className="modal-header border-bottom-0 p-4">
          <h3>
            <FormattedMessage
              defaultMessage="Подтвердите отправку доставок на склад"
              id="deliveryOrderMarkAsReceivedBulk.btn-label.confirmReceived"
              description="Надпись на кнопке/заголовок"
            />
          </h3>
          <button
            type="button"
            className="btn-close"
            onClick={() => props.reject()}
          />
        </div>
        <div className="modal-body p-4 pt-0">
          {props.deliveryOrders.map((order) => {
            return (
              <div key={order.id} className={' text-center fs-21'}>
                D{order.internalId}-{order.user?.internalId}-{order.count}
              </div>
            );
          })}

          <Controller
            control={control}
            name="packedFiles"
            render={({ field, fieldState }) => (
              <InputWrapper
                title={intl.formatMessage({
                  defaultMessage: 'Прикрепите фотографии принятых доставок',
                  id: 'markAsReceivedBulkModal.input-label.photoOfThePackagedGoods',
                  description: 'Заголовок поля ввода',
                })}
                className="mb-3"
                error={fieldState.error?.message}
              >
                <MediaInput
                  {...field}
                  multiple
                  enableClipboardPasteHandler={false}
                />
              </InputWrapper>
            )}
          />

          <InputWrapper
            theme="light"
            className="mt-3 mb-3"
            title={intl.formatMessage({
              defaultMessage: 'Место хранения',
              id: 'input.label.storageLocation',
              description: 'Заголовок поля ввода',
            })}
            error={errors.storageLocation?.message}
          >
            <Controller
              name="storageLocation"
              control={control}
              rules={{
                maxLength: {
                  value: 20,
                  message: 'Максимальная длина 20 символов',
                },
              }}
              render={({ field }) => (
                <CreatableSelect
                  {...field}
                  options={storageLocations?.map((location) => ({
                    value: location,
                    label: location,
                  }))}
                  placeholder=""
                  formatCreateLabel={(value) => `Добавить "${value}"`}
                  onInputChange={(newValue) => {
                    setInputStorage(newValue);
                  }}
                  onChange={(newValue) => {
                    setInputStorage('');
                    field.onChange(newValue);
                  }}
                  onBlur={() => {
                    if (inputStorage) {
                      field.onChange({
                        value: inputStorage,
                        label: inputStorage,
                      });
                    }
                  }}
                />
              )}
            />
          </InputWrapper>

          <Controller
            name="date"
            control={control}
            rules={{
              ...required,
            }}
            render={({ field, fieldState }) => {
              return (
                <InputWrapper
                  title={intl.formatMessage({
                    defaultMessage: 'Укажите дату приемки на склад',
                    id: 'markAsReceivedBulkModal.input-label.date',
                    description: 'Заголовок поля ввода',
                  })}
                  error={fieldState.error?.message}
                  required
                >
                  <DatePicker
                    className={'w-100'}
                    format={'dd.MM.yyyy HH:mm'}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Выберите дату',
                      id: 'markAsReceivedBulkModal.title.SelectDate',
                      description: 'Пояснение к полю ввода',
                    })}
                    isoWeek={true}
                    onChange={(v) => {
                      if (!v) {
                        field.onChange(null);
                      } else {
                        field.onChange(
                          moment(v).format('DD.MM.YYYY HH:mm') as RawDateValue,
                        );
                      }
                    }}
                    defaultValue={field.value}
                  ></DatePicker>
                </InputWrapper>
              );
            }}
          />
        </div>

        <div className="modal-footer pt-3 p-4 justify-content-center d-flex gap-2">
          <div className="btn btn-light" onClick={() => props.close()}>
            <FormattedMessage
              defaultMessage="Отмена"
              id="deliveryOrder.cancel"
              description="Надпись на кнопке"
            />
          </div>
          <button
            type="submit"
            className="btn btn-success"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Подтвердить отправку"
                id="deliveryAssemblyModal.btn-label.confirmShipment"
                description="Надпись на кнопке"
              />
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
